import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Avatar,
    Paper,
    Divider,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface Organization {
    _id: string;
    name: string;
    subdomain: string;
    logoUrl?: string;
}

interface SubscriptionModalProps {
    open: boolean;
    onClose: () => void;
    offerId: string | null;
    offerTitle: string | null;
    onSuccess?: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ open, onClose, offerId, offerTitle, onSuccess }) => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    useEffect(() => {
        if (open) {
            fetchOrganizations();
            setSelectedOrg('');
        }
    }, [open]);

    const fetchOrganizations = async () => {
        try {
            const res = await api.get('/api/organizations');
            setOrganizations(res.data);
        } catch (err) {
            console.error("Error fetching organizations", err);
        }
    };

    const handleSubscribe = async () => {
        if (!offerId || !selectedOrg) return;
        setLoading(true);
        try {
            await api.post('/api/subscription', {
                organization: selectedOrg,
                offer: offerId
            });
            onClose();
            if (onSuccess) onSuccess();
            showToast('Offre assignée avec succès', 'success');
        } catch (err) {
            console.error("Error creating subscription", err);
            showToast('Erreur lors de l\'assignation de l\'offre', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                        <PersonAddIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                            Assigner l'offre
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Lier une offre à une organisation
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 4 }}>
                {/* Offer Summary */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: '#f5f7fa',
                        borderRadius: 2,
                        border: '1px dashed #667eea',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Avatar sx={{ bgcolor: '#e8eaf6', color: '#667eea' }}>
                        <LocalOfferIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            Offre sélectionnée
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a237e' }}>
                            {offerTitle}
                        </Typography>
                    </Box>
                </Paper>

                <Divider sx={{ mb: 3 }} />

                <FormControl fullWidth>
                    <InputLabel id="org-select-label">Sélectionner une organisation</InputLabel>
                    <Select
                        labelId="org-select-label"
                        value={selectedOrg}
                        label="Sélectionner une organisation"
                        onChange={(e) => setSelectedOrg(e.target.value)}
                        sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: 2,
                            }
                        }}
                    >
                        {organizations.map((org) => (
                            <MenuItem key={org._id} value={org._id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <BusinessIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {org.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {org.subdomain}.edujalm.com
                                        </Typography>
                                    </Box>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
                <Button onClick={onClose} sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSubscribe}
                    variant="contained"
                    disabled={!selectedOrg || loading}
                    startIcon={<PersonAddIcon />}
                    sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                        },
                        '&:disabled': {
                            background: '#e0e0e0',
                        }
                    }}
                >
                    {loading ? 'Assignation...' : 'Assigner l\'offre'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionModal;
